import React, { useEffect, useRef, useState } from "react";
import "../../style/GroupChat/ChatRoomPage.scss";

import ChatTopBar from "../../components/GroupChat/ChatTopBar";
import UserDetail from "../../components/GroupChat/UserDetail";
import MessageItem from "../../components/GroupChat/MessageItem";
import FileItem from "../../components/GroupChat/FileItem";
import PictureItem from "../../components/GroupChat/PictureItem";
import ShowToast from "../../components/GroupChat/ShowToast";
import InputFileUpload from "../../components/GroupChat/InputFileUpload";
import ProgressBarComponent from "../../components/GroupChat/ProgressBarComponent";

import { Toaster, toast } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";

import { socket } from "../../socket/ConnectSocket";
import { getFileSize } from "../../helper/GetFileSize";
import { getTime } from "../../helper/GetTime";


import {
  onConnectEvent,
  onDisconnectEvent,
  onJoinRoomEvent,
  onToastEvent,
  onErrorEvent,
  onSendMessageEvent,
  onReceiveMessageEvent,
  onGetRoomUsersEvent,
  onReceiveRoomUsersEvent,
  onSendStartTypingEvent,
  onSendStopTypingEvent,
  onGetStartTypingEvent,
  onGetStopTypingEvent,
} from "../../socket/SocketEvents";

import {
  allowedFileTypes,
  allowedPictureTypes,
  allowedVideoTypes,
  allowedAudioTypes,
} from "../../utils/AllowedInputTypes";

function ChatRoomPage() {
  let IS_NEW_ROOM, USER_NAME, USER_ID, ROOM_CODE;

  const navigate = useNavigate();
  const location = useLocation();

  if (location.state === null) {
    toast.error(`Not a Auth user`);
    navigate("/");
  } else {
    IS_NEW_ROOM = location.state.IS_NEW_ROOM;
    USER_NAME = location.state.USER_NAME;
    USER_ID = location.state.USER_ID;
    ROOM_CODE = location.state.ROOM_CODE;
  }

  const [messageList, setMessageList] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [userList, setUserList] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typerID, setTyperId] = useState("");
  const [isSomeoneTyping, setIsSomeoneTyping] = useState(false);
  const [isDivVisible, setIsDivVisible] = useState(false);

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const pictureInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);

  const [isMediaSending, setIsMediaSending] = useState(false);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behaviour: "smooth" });
    }
  }, [messageList]);

  useEffect(() => {
    // Set up the socket.io event listeners within this useEffect.

    const handleReceiveMessage = (data) => {
      onReceiveMessageEvent(data, setMessageList);
    };

    const handleReceiveRoomUsers = (data) => {
      onReceiveRoomUsersEvent(data, setUserList);
    };

    const handleGetStartTyping = (data) => {
      onGetStartTypingEvent(data, setTyperId, setIsSomeoneTyping);
    };

    const handleGetStopTyping = (data) => {
      onGetStopTypingEvent(data, setTyperId, setIsSomeoneTyping);
    };

    const handleDisconnect = () => {
      onDisconnectEvent(socket, setIsConnected);
    };

    socket.on("connect", () => onConnectEvent(socket, setIsConnected));
    socket.emit("joinRoomEvent", onJoinRoomEvent(IS_NEW_ROOM, USER_NAME, USER_ID, ROOM_CODE));
    socket.on("toastEvent", (msg) => onToastEvent(msg, setMessageList));
    socket.on("errorEvent", (msg) => onErrorEvent(msg, navigate));
    socket.on("receiveMessageEvent", handleReceiveMessage);
    socket.emit("getRoomUsersEvent", onGetRoomUsersEvent(ROOM_CODE));
    socket.on("receiveRoomUsersEvent", handleReceiveRoomUsers);
    socket.on("getStartTypingEvent", handleGetStartTyping);
    socket.on("getStopTypingEvent", handleGetStopTyping);
    socket.on("disconnect", handleDisconnect);

    return () => {
      // Clean up the event listeners when the component unmounts.

      socket.off("connect", () => onConnectEvent(socket, setIsConnected));
      socket.off("toastEvent", (msg) => onToastEvent(msg, setMessageList));
      socket.off("errorEvent", (msg) => onErrorEvent(msg, navigate));
      socket.off("receiveMessageEvent", handleReceiveMessage);
      socket.off("receiveRoomUsersEvent", handleReceiveRoomUsers);
      socket.off("getStartTypingEvent", handleGetStartTyping);
      socket.off("getStopTypingEvent", handleGetStopTyping);
      socket.off("disconnect", handleDisconnect);
    };
  }, [IS_NEW_ROOM, USER_NAME, USER_ID, ROOM_CODE]);


  const handleFileIconClick = function () {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    setIsMediaSending(true); // SET SENDING STATUS TO TRUE

    const file = e.target.files[0];
    if (file && allowedFileTypes.includes(file.type)) {
      setSelectedFile(file);

      const FILE_SIZE = getFileSize(file.size);
      const sizeDetail = FILE_SIZE.split(" ");
      if (sizeDetail[1] === "MB" && sizeDetail[0] > 50) {
        toast.error(`File size is restricted to a maximum of 50 MB.`);
        return;
      }
      const TIME = getTime(new Date());

      const data = {
        TYPE: "FILE",
        USER_NAME: USER_NAME,
        USER_ID: USER_ID,
        ROOM_CODE: ROOM_CODE,
        CONTENT: file,
        CONTENTBASE64: "",
        CONTENT_NAME: file.name,
        CONTENT_SIZE: FILE_SIZE,
        TIME: TIME,
      };

      setMessageList((prevData) => [...prevData, data]);

      socket.emit(
        "sendMessageEvent",
        onSendMessageEvent(
          "FILE",
          USER_NAME,
          USER_ID,
          ROOM_CODE,
          file,
          "",
          file.name,
          FILE_SIZE,
          TIME
        )
      );

      setSelectedFile(null); // RESET THE SELECTED FILE
    } else {
      setSelectedFile(null);
      // OPTIONALLY SHOW AN ERROR MESSAGE OR PROVIDE FEEDBACK TO THE USER.
      toast.error(
        `Invalid file type. Please select a .doc, .docx, .xml, .txt, or .pdf file. 😑`
      );
    }

    setIsMediaSending(false); // SET SENDING STATUS BACK TO FALSE
    setIsDivVisible(false);
  };

  const handlePictureIconClick = function () {
    pictureInputRef.current?.click();
  };

  const handlePictureChange = (e) => {
    setIsMediaSending(true); // SET SENDING STATUS TO TRUE

    const file = e.target.files[0];
    if (file && allowedPictureTypes.includes(file.type)) {
      const reader = new FileReader();

      reader.onloadend = () => {
        // CONVERT THE PICTURE TO BASE64 DATA BEFORE SENDING
        const base64Data = reader.result.split(",")[1];
        const FILE_SIZE = getFileSize(file.size);
        const sizeDetail = FILE_SIZE.split(" ");
        if (sizeDetail[1] === "MB" && sizeDetail[0] > 20) {
          toast.error(`Picture size is restricted to a maximum of 20 MB.`);
          return;
        }
        const TIME = getTime(new Date());

        const data = {
          TYPE: "PICTURE",
          USER_NAME: USER_NAME,
          USER_ID: USER_ID,
          ROOM_CODE: ROOM_CODE,
          CONTENT: file,
          CONTENTBASE64: base64Data,
          CONTENT_NAME: file.name,
          CONTENT_SIZE: FILE_SIZE,
          TIME: TIME,
        };

        setMessageList((prevData) => [...prevData, data]);

        // EMIT THE "PICTURE" EVENT TO THE SERVER WITH THE PICTURE DATA
        socket.emit(
          "sendMessageEvent",
          onSendMessageEvent(
            "PICTURE",
            USER_NAME,
            USER_ID,
            ROOM_CODE,
            file,
            base64Data, // SENDING THE BASE64 DATA OF THE PICTURE
            file.name,
            FILE_SIZE,
            TIME
          )
        );

        setSelectedPicture(URL.createObjectURL(file)); // RESET THE SELECTED PICTURE
      };

      reader.readAsDataURL(file);
    } else {
      setSelectedPicture(null);
      // OPTIONALLY SHOW AN ERROR MESSAGE OR PROVIDE FEEDBACK TO THE USER.
      toast.error(
        `Invalid file type. Please select an image (JPEG, PNG, SVG) or GIF file. 😑`
      );
    }

    setIsMediaSending(false); // SET SENDING STATUS BACK TO FALSE
    setIsDivVisible(false);
  };

  const handleVideoIconClick = function () {
    videoInputRef.current?.click();
  };

  const handleVideoChange = (e) => {
    setIsMediaSending(true); // SET SENDING STATUS TO TRUE

    const file = e.target.files[0];
    if (file && allowedVideoTypes.includes(file.type)) {
      setSelectedVideo(URL.createObjectURL(file));

      const FILE_SIZE = getFileSize(file.size);
      const sizeDetail = FILE_SIZE.split(" ");
      if (sizeDetail[1] === "MB" && sizeDetail[0] > 100) {
        toast.error(`Video size is restricted to a maximum of 100 MB.`);
        return;
      }
      const TIME = getTime(new Date());

      const data = {
        TYPE: "VIDEO",
        USER_NAME: USER_NAME,
        USER_ID: USER_ID,
        ROOM_CODE: ROOM_CODE,
        CONTENT: file,
        CONTENTBASE64: "",
        CONTENT_NAME: file.name,
        CONTENT_SIZE: FILE_SIZE,
        TIME: TIME,
      };

      setMessageList((prevData) => [...prevData, data]);

      socket.emit(
        "sendMessageEvent",
        onSendMessageEvent(
          "VIDEO",
          USER_NAME,
          USER_ID,
          ROOM_CODE,
          file,
          "",
          file.name,
          FILE_SIZE,
          TIME
        )
      );

      setSelectedVideo(null); // RESET THE SELECTED VIDEO
    } else {
      setSelectedVideo(null);
      // OPTIONALLY SHOW AN ERROR MESSAGE OR PROVIDE FEEDBACK TO THE USER.
      toast.error(
        `Invalid file type. Please select a video (MP4, WebM, Ogg) file. 😑`
      );
    }

    setIsMediaSending(false); // SET SENDING STATUS BACK TO FALSE
    setIsDivVisible(false);
  };

  const handleAudioIconClick = function () {
    audioInputRef.current?.click();
  };

  const handleAudioChange = (e) => {
    setIsMediaSending(true); // SET SENDING STATUS TO TRUE

    const file = e.target.files[0];
    if (file && allowedAudioTypes.includes(file.type)) {
      setSelectedAudio(URL.createObjectURL(file));

      const FILE_SIZE = getFileSize(file.size);
      const sizeDetail = FILE_SIZE.split(" ");
      if (sizeDetail[1] === "MB" && sizeDetail[0] > 50) {
        toast.error(`Audio size is restricted to a maximum of 50 MB.`);
        return;
      }
      const TIME = getTime(new Date());

      const data = {
        TYPE: "AUDIO",
        USER_NAME: USER_NAME,
        USER_ID: USER_ID,
        ROOM_CODE: ROOM_CODE,
        CONTENT: file,
        CONTENTBASE64: "",
        CONTENT_NAME: file.name,
        CONTENT_SIZE: FILE_SIZE,
        TIME: TIME,
      };

      setMessageList((prevData) => [...prevData, data]);

      socket.emit(
        "sendMessageEvent",
        onSendMessageEvent(
          "AUDIO",
          USER_NAME,
          USER_ID,
          ROOM_CODE,
          file,
          "",
          file.name,
          FILE_SIZE,
          TIME
        )
      );

      setSelectedAudio(null); // RESET THE SELECTED AUDIO
    } else {
      setSelectedAudio(null);
      // OPTIONALLY SHOW AN ERROR MESSAGE OR PROVIDE FEEDBACK TO THE USER.
      toast.error(
        `Invalid file type. Please select an audio file (MP3, Ogg, WAV, WebM) file. 😑`
      );
    }

    setIsMediaSending(false); // SET SENDING STATUS BACK TO FALSE
    setIsDivVisible(false);
  };

  const handleClipClick = function () {
    // TOGGLE CHATROOM__FLOAT__MENU DIV VISIBILITY
    setIsDivVisible((prevVisibility) => !prevVisibility);
  };

  const handleInputKeyDown = function (e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    } else if (e.key === 'Enter' && e.shiftKey) {
      // Insert a newline character when Enter is pressed with Shift
      setNewMessage((prevMessage) => prevMessage + '\n');
    }
  };

  const handleSendMessage = function (e) {
    e.preventDefault();
    if (newMessage.trim() !== "") {
      const TIME = getTime(new Date());

      const data = {
        TYPE: "MESSAGE",
        USER_NAME: USER_NAME,
        USER_ID: USER_ID,
        ROOM_CODE: ROOM_CODE,
        CONTENT: newMessage,
        CONTENTBASE64: "",
        CONTENT_NAME: "",
        CONTENT_SIZE: "",
        TIME: TIME,
      };

      setMessageList((prevData) => [...prevData, data]);

      socket.emit(
        "sendMessageEvent",
        onSendMessageEvent(
          "MESSAGE",
          USER_NAME,
          USER_ID,
          ROOM_CODE,
          newMessage,
          "",
          "",
          "",
          TIME
        )
      );
      setNewMessage("");
      setIsDivVisible(false);
    }
  };

  // FUNCTION TO EMIT STARTTYPING EVENT
  const startTyping = () => {
    if (!isTyping) {
      socket.emit(
        "sendStartTypingEvent",
        onSendStartTypingEvent(USER_NAME, USER_ID, ROOM_CODE)
      );
      setIsTyping(true);
    }
  };

  // FUNCTION TO EMIT STOPTYPING EVENT
  const stopTyping = () => {
    if (isTyping) {
      socket.emit(
        "sendStopTypingEvent",
        onSendStopTypingEvent(USER_NAME, USER_ID, ROOM_CODE)
      );
      setIsTyping(false);
    }
  };

  function handleResize() {
    if (window.innerWidth <= 768) {
      document.querySelector(".App .sidebarLeft").style.display = "none";
      document.querySelector(".App .centerComponent").style.height = "100vh";
    } else {
      document.querySelector(".App .sidebarLeft").style.display = "block"; // Restore sidebar
      document.querySelector(".App .centerComponent").style.height = "100vh";
    }
  }

  // Initial check
  handleResize();

  // Add event listener for window resize
  window.addEventListener("resize", handleResize);

  return (
    <div className="chatroom__page">
      <Toaster position="top-center" reverseOrder={false}></Toaster>

      <div className="chatroom__menu">
        <UserDetail
          userList={userList}
          USER_ID={USER_ID}
          typerID={typerID}
          ROOM_CODE={ROOM_CODE}
          isSomeoneTyping={isSomeoneTyping}
        />
      </div>

      <div className="chatroom__contents">
        <div>
          <ChatTopBar ROOM_CODE={ROOM_CODE} userList={userList} isTyping={isTyping} />

          <div className="chatroom__box">
            {/* RENDER PROGRESS BAR WHEN SENDING MEDIA */}
            {isMediaSending && <ProgressBarComponent />}

            {messageList.map((data, index) =>
              typeof data === "object" ? (
                <div key={index} ref={scrollRef}>
                  {(() => {
                    switch (data.TYPE) {
                      case "MESSAGE":
                        return (
                          <MessageItem
                            isAuthor={
                              USER_ID === data.USER_ID ? "you" : "other"
                            }
                            data={data}
                          />
                        );

                      case "FILE":
                        return (
                          <FileItem
                            isAuthor={
                              USER_ID === data.USER_ID ? "you" : "other"
                            }
                            data={data}
                          />
                        );

                      case "PICTURE":
                        return (
                          <PictureItem
                            isAuthor={
                              USER_ID === data.USER_ID ? "you" : "other"
                            }
                            data={data}
                          />
                        );

                      case "VIDEO":
                        return (
                          <FileItem
                            isAuthor={
                              USER_ID === data.USER_ID ? "you" : "other"
                            }
                            data={data}
                          />
                        );

                      case "AUDIO":
                        return (
                          <FileItem
                            isAuthor={
                              USER_ID === data.USER_ID ? "you" : "other"
                            }
                            data={data}
                          />
                        );
                    }
                  })()}
                </div>
              ) : (

                <div className="Welcome">
                  <ShowToast key={index} data={data} />
                </div>

              )
            )}
          </div>
        </div>

        <div className="chatroom__input">
          <div className="chatroom__attachment__menu">
            {isDivVisible && (
              <div className="chatroom__float__menu">
                <div
                  className="wrapper file__wrapper"
                  title="File"
                  onClick={handleFileIconClick}
                >
                  <i className="fas fa-file"></i>
                </div>
                <div
                  className="wrapper picture__wrapper"
                  title="Picture"
                  onClick={handlePictureIconClick}
                >
                  <i className="far fa-image"></i>
                </div>
                <div
                  className="wrapper video__wrapper"
                  title="Video"
                  onClick={handleVideoIconClick}
                >
                  <i className="fas fa-play"></i>
                </div>
                <div
                  className="wrapper audio__wrapper"
                  title="Audio"
                  onClick={handleAudioIconClick}
                >
                  <i className="fas fa-headphones"></i>
                </div>
              </div>
            )}

            <div className="chatroom__clip" onClick={handleClipClick}>
              <i className="lni lni-paperclip"></i>
            </div>
          </div>

          <div className="chatroom__textarea__div">
            <textarea
              className="chatroom__textarea"
              placeholder="Type your message here ..."
              type="text"
              autoFocus="Yes"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onInput={startTyping}
              onBlur={stopTyping}
            />
          </div>

          <div className="chatroom__send" onClick={handleSendMessage}>
            <span className="material-symbols-outlined">send</span>
          </div>

          <InputFileUpload
            ref={{
              fileInputRef,
              pictureInputRef,
              videoInputRef,
              audioInputRef,
            }}
            handleFileChange={handleFileChange}
            handlePictureChange={handlePictureChange}
            handleVideoChange={handleVideoChange}
            handleAudioChange={handleAudioChange}
          />
        </div>
      </div>
    </div>
  );
}

export default ChatRoomPage;
